import { broodSystem } from './systems/broodSystem';
import { collisionSystem } from './systems/collisionSystem';
import { damageSystem } from './systems/damageSystem';
import { emitterSystem } from './systems/emitterSystem';
import { endlessSystem } from './systems/endlessSystem';
import { enemyBulletSystem } from './systems/enemyBulletSystem';
import { levelSystem } from './systems/levelSystem';
import { motionSystem } from './systems/motionSystem';
import { obstacleSystem } from './systems/obstacleSystem';
import { phaseSystem } from './systems/phaseSystem';
import { pickupSystem } from './systems/pickupSystem';
import { playerSystem } from './systems/playerSystem';
import { progressionSystem } from './systems/progressionSystem';
import { rankSystem } from './systems/rankSystem';
import { secondarySystem } from './systems/secondarySystem';
import { spawnSystem } from './systems/spawnSystem';
import type { InputFrame, World } from './World';

/**
 * UN tick de simulation — 1/120 s exactement.
 *
 * ⚠️ L'ORDRE DES SYSTÈMES EST FIGÉ. Le modifier change le résultat de la
 * simulation à entrées identiques, et invalide donc tous les replays, tous les
 * golden tests et tous les scores vérifiés. Ce n'est pas une préférence de
 * style : c'est une partie du format de sauvegarde.
 *
 * Il se lit comme une chaîne de causalité :
 *   1. le joueur agit          (entrées → mouvement, tir)
 *   2. le monde se peuple      (vagues)
 *   3. les ennemis agissent    (émission de projectiles)
 *   4. tout se déplace         (intégration, élimination hors champ)
 *   5. on constate les contacts (collisions, dégâts, graze)
 *   6. on constate les morts    (destructions, butin, score)
 *   7. on ramasse               (butin collecté, slots rendus)
 *
 * Le ramassage vient APRÈS les morts, et non avant : un ennemi tué ce tick doit
 * pouvoir voir son butin ramassé dès le tick suivant, jamais deux ticks plus
 * tard. L'ordre inverse introduirait une latence d'un tick sur toute la chaîne
 * économique.
 *
 * L'invariant qui compte : cette fonction est PURE au sens qui nous intéresse —
 * même monde + mêmes entrées ⇒ même état final, sur n'importe quel moteur JS,
 * à n'importe quelle vitesse, avec ou sans GPU. C'est ce qui rend possibles les
 * tests headless, l'attract mode, « revoir la mort » et la re-simulation
 * serveur du leaderboard.
 */
export function step(w: World, input: InputFrame): void {
  // Mémorisation des positions d'avant-tick. En tête de step plutôt qu'à la
  // charge de l'appelant : la collision balayée et l'interpolation en dépendent
  // toutes les deux, et un appelant qui l'oublierait produirait des tunnels
  // silencieux.
  w.savePrev();

  playerSystem(w, input);
  // Immédiatement après le joueur : la secondaire part de sa position à jour,
  // et avant que le monde ne se peuple, pour qu'elle ne puisse pas toucher un
  // ennemi apparu dans le même tick — ce qui donnerait des morts survenues
  // avant l'entrée en scène.
  secondarySystem(w, input);
  // Le niveau peuple le monde ; `spawnSystem` reste pour le banc de charge.
  // `endlessSystem` est le pendant procédural de `levelSystem` pour le mode sans
  // fin : les deux gardent en tête (`run.levelId` d'un côté, `run.endless` de
  // l'autre) et sont mutuellement exclusifs, donc un seul peuple à la fois. Ils
  // sont tous deux des no-op inertes quand leur mode n'est pas actif.
  levelSystem(w);
  endlessSystem(w);
  spawnSystem(w);
  // Les ennemis qui en engendrent d'autres (pondeurs, œufs) le font ICI, dans la
  // phase de peuplement : un engendré né ce tick porte son télégraphe et n'agit
  // donc qu'aux ticks suivants, jamais dans le même souffle que sa naissance.
  broodSystem(w);
  // Les ennemis pilotent d'abord leurs projectiles DÉJÀ en vol (guidage, courbe,
  // ancrage du faisceau), puis en émettent de nouveaux. L'ordre est le pendant
  // exact de celui du joueur (`secondarySystem` conduit avant d'émettre) : un
  // projectile né ce tick-ci ne se dirige qu'au suivant, ce qui préserve sa
  // fraction de vol en ligne droite. Les deux tournent AVANT `motionSystem`, qui
  // seul avance les positions — ce système ne fait que ré-orienter des vitesses.
  enemyBulletSystem(w);
  emitterSystem(w);
  motionSystem(w);
  // Le décor collisionnable descend avec le défilement, dans la même phase que
  // tout ce qui bouge et JUSTE avant les collisions — pour que `collisionSystem`
  // lise le segment balayé (px,py)→(x,y) que ce système vient de tracer. Un
  // monde sans obstacle (tous les tests de moteur, le golden) le traverse comme
  // un no-op : aucune allocation, aucun tirage RNG, aucun état modifié.
  obstacleSystem(w);
  collisionSystem(w);
  damageSystem(w);
  // Phases de boss : lit les PV FINALISÉS du tick et fait avancer la phase de
  // combat pour le peuplement du tick suivant (quel émetteur tire, dans
  // emitterSystem). Placé après la mort pour ne jamais phaser un cadavre.
  phaseSystem(w);
  pickupSystem(w);
  progressionSystem(w);
  // Le rang caché ferme la chaîne : il lit les compteurs de performance FINALISÉS
  // du tick (kills, coups reçus, morts, frôlements) et écrit `run.rankMul` pour le
  // peuplement du tick SUIVANT (la cadence ennemie, dans emitterSystem). En
  // dernier, il voit des compteurs complets et n'influence l'ordre d'allocation
  // d'aucun autre système.
  rankSystem(w);

  w.tick++;
}
